import React from 'react';
import InputRange, { Range } from 'react-input-range';
import Div from '../Element/Div/Div';
import styles from './RangeSlider.module.scss';
import Flex from '../Flex/Flex';
import BodyText from '../Typography/BodyText/BodyText';
import Gutter from '../Gutter/Gutter';
import { CreateProps } from '../../types/utils/CreateProps';

const defaultClassNames = {
  activeTrack: styles.inputRange_track_activeTrack,
  disabledInputRange: styles.inputRange__disabled,
  inputRange: styles.inputRange,
  labelContainer: styles.inputRange_label,
  maxLabel: styles.inputRange_label__max,
  minLabel: styles.inputRange_label__min,
  slider: styles.inputRange_sliderContainer_slider,
  sliderContainer: styles.inputRange_sliderContainer,
  track: styles.inputRange_track,
  valueLabel: styles.inputRange_label__value,
};

export interface InputRangeClassNamesShape {
  activeTrack: string;
  disabledInputRange: string;
  inputRange: string;
  labelContainer: string;
  maxLabel: string;
  minLabel: string;
  slider: string;
  sliderContainer: string;
  track: string;
  valueLabel: string;
}

type RangeSliderProps = CreateProps<
  {
    /** Disabled range slider. Defaults to false. */
    disabled?: boolean;
    /** Minimun (starting) range value */
    minRangeValue: number;
    /** Maximum (ending) range value */
    maxRangeValue: number;
    /** Value (state) of range slider */
    value: Range | number;
    /** Handler function for changing value */
    onChange: (value: number | Range) => void;
    /** Whenever your user starts interacting with your component(i.e.: onMouseDown, or onTouchStart), this function gets called. */
    onChangeStart?: (value: number | Range) => void;
    /** Every mouse / touch event can trigger multiple updates, therefore causing onChange callback to fire multiple times. On the other hand, onChangeComplete callback only gets called when the user stops dragging. */
    onChangeComplete?: (value: number | Range) => void;
    /** Amount of increment/decrement */
    interval?: number;
    /** By default, value labels are displayed as plain numbers. If you want to change the display, you can do so by passing in a function. The function can return something different, i.e.: append a unit, reduce the precision of a number.
  For example: (value) => `${value} €` */
    formatLabel?: (value: number | Range) => string;
    /** Set to true to allow 2-value slider minimum (value.min) and maximum (value.max) to be equal. */
    allowSameValues?: boolean;
    /** Set aria - labelledby attribute to your component. */
    ariaLabelledby?: string;
    /** Set aria - controls attribute to your component. */
    ariaControls?: string;
    /** If this property is set to true, you can drag the entire track definded by range values. Otherwise clicking the track changes the closest value. */
    draggableTrack?: boolean;
    /** If set, creates input element with given name for form usage. */
    name?: string;
    /** If set, overrides default InputRange component classNames. See RangeSlider.module.scss for the default styles. */
    InputRangeClassNames?: InputRangeClassNamesShape;
  },
  typeof Div,
  'padding'
>;

const RangeSlider: React.FunctionComponent<RangeSliderProps> = ({
  disabled = false,
  minRangeValue,
  maxRangeValue,
  value,
  onChange,
  onChangeStart = undefined,
  onChangeComplete = undefined,
  interval = 1,
  formatLabel = undefined,
  allowSameValues = false,
  ariaLabelledby = undefined,
  ariaControls = undefined,
  draggableTrack = false,
  name = undefined,
  InputRangeClassNames = defaultClassNames,
  ...props
}) => {
  // The react-input-range does not correctly check if the value is under or over limits.
  // Below code just checks that value is between the limits and in-case of violation changes value to the limit.

  if ((value as Range).min != null) {
    (value as Range).min = Math.max((value as Range).min, minRangeValue);
    (value as Range).min = Math.min((value as Range).min, maxRangeValue);
    (value as Range).max = Math.max((value as Range).max, minRangeValue);
    (value as Range).max = Math.min((value as Range).max, maxRangeValue);
  } else {
    value = Math.max(value as number, minRangeValue);
    value = Math.min(value, maxRangeValue);
  }

  // Create own custom labels to be displayed
  const minDisplay =
    (value as Range).min != null ? (
      <BodyText
        tagName="span"
        size="sm"
        fontWeight={'medium'}
        style={{ letterSpacing: '0.5px' }}
        color={disabled ? 'gray400' : 'black'}
      >
        {formatLabel ? formatLabel((value as Range).min) : (value as Range).min}
      </BodyText>
    ) : null;
  const maxDisplay = (
    <BodyText
      tagName="span"
      size="sm"
      fontWeight={'medium'}
      color={disabled ? 'gray400' : 'black'}
      style={{ marginLeft: 'auto', letterSpacing: '0.5px' }}
    >
      {formatLabel
        ? formatLabel((value as Range).max || value)
        : (value as Range).max || value}
    </BodyText>
  );
  return (
    <Div padding={{ horizontal: 'xs' }} {...props}>
      <InputRange
        classNames={InputRangeClassNames}
        disabled={disabled}
        minValue={minRangeValue}
        maxValue={maxRangeValue}
        value={value}
        onChange={onChange}
        onChangeStart={onChangeStart}
        onChangeComplete={onChangeComplete}
        step={interval}
        formatLabel={formatLabel}
        allowSameValues={allowSameValues}
        ariaLabelledby={ariaLabelledby}
        ariaControls={ariaControls}
        draggableTrack={draggableTrack}
        name={name}
      />
      <Gutter size="xs" />
      <Flex spaceBetween={true}>
        {minDisplay}
        {maxDisplay}
      </Flex>
    </Div>
  );
};

export default RangeSlider;
